import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '../config/environment';
import LoadingState from './LoadingState';

const OnboardingRouter: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const checkOnboardingStatus = useCallback(async () => {
    try {
      let flowId = searchParams.get('flow') || searchParams.get('session') || searchParams.get('flowId');
      const existingFlowId = localStorage.getItem('onboardingFlowId');

      if (flowId) {
        localStorage.setItem('onboardingFlowId', flowId);
        const flowData = await checkFlowStatus(flowId);
        redirectBasedOnFlowData(flowData, flowId);
      } else if (existingFlowId) {
        const flowData = await checkFlowStatus(existingFlowId);
        redirectBasedOnFlowData(flowData, existingFlowId);
      } else {
        const urlParams = new URLSearchParams(searchParams);
        const paramString = urlParams.toString();
        const targetUrl = `/getting-started${paramString ? `?${paramString}` : ''}`;
        navigate(targetUrl);
      }
    } catch (error) {
      console.error('Error checking onboarding status:', error);
      navigate('/getting-started');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const checkFlowStatus = async (flowId: string) => {
    try {
      const response = await fetch(`${config.api.onboardingFlows}/${flowId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const flow = await response.json();
        return flow;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error fetching flow status:', error);
      return null;
    }
  };

  const redirectBasedOnFlowData = (flowData: any, flowId: string) => {
    if (!flowData) {
      localStorage.removeItem('onboardingFlowId');
      navigate('/getting-started');
      return;
    }

    const { status } = flowData;
    const flowParam = `?flow=${flowId}`;

    switch (status) {
      case 'INITIATED':
        if (!flowData.emailVerifiedVia) {
          navigate(`/verify-email${flowParam}`);
        } else {
          navigate(`/select-plans${flowParam}`);
        }
        break;
      case 'EMAIL_VERIFIED':
        navigate(`/select-plans${flowParam}`);
        break;
      case 'PAID':
        navigate(`/create-organization${flowParam}`);
        break;
      case 'CREATING_ORG':
        navigate(`/create-organization${flowParam}`);
        break;
      case 'CONFIGURING_STRUCTURE':
        navigate(`/configure-structure${flowParam}`);
        break;
      case 'COMPLETE':
        navigate(`/complete${flowParam}`);
        break;
      case 'FAILED':
        navigate(`/getting-started${flowParam}`);
        break;
      default:
        console.warn('OnboardingRouter - Unknown status:', status);
        navigate(`/getting-started${flowParam}`);
    }
  };

  if (loading) {
    return <LoadingState message="Setting up your onboarding..." />;
  }

  return null;
};

export default OnboardingRouter;
